import React, { useCallback, useEffect, useState } from 'react'
import { SMBeamModel, BeamMaterial } from '../types'
import { persistModel, isValidBeamModel } from '../modelPersistence'
import { listDesigns, saveDesign, loadDesign, deleteDesign, DesignSummary } from '../beamDesignsApi'

interface DesignManagerBarProps {
  material: BeamMaterial
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  getSessionHeaders: () => Record<string, string>
  // Optional: when provided, a "Share" button appears for the selected saved design.
  // The host app opens its sharing modal. Omitted by apps without sharing wired.
  onShare?: (design: { id: string; name: string }) => void
}

interface StatusMessage {
  kind: 'ok' | 'err'
  text: string
}

// Save / load / delete named beam designs for the current material. The whole model
// (geometry, section, reinforcement incl. stirrups, loads) is persisted via the backend.
const DesignManagerBar: React.FC<DesignManagerBarProps> = ({ material, model, setModel, getSessionHeaders, onShare }) => {
  const [designs, setDesigns] = useState<DesignSummary[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [busy, setBusy] = useState<boolean>(false)
  const [status, setStatus] = useState<StatusMessage | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setDesigns(await listDesigns(material, getSessionHeaders()))
    } catch (error) {
      setStatus({ kind: 'err', text: (error as Error).message })
    }
  }, [material, getSessionHeaders])

  useEffect(() => {
    refresh()
  }, [refresh])

  const onSave = useCallback(async (): Promise<void> => {
    const name = (model.designName || '').trim()
    if (!name) {
      setStatus({ kind: 'err', text: 'Enter a design name before saving.' })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const saved = await saveDesign(material, name, model, getSessionHeaders())
      await refresh()
      setSelectedId(saved.id)
      setStatus({ kind: 'ok', text: `Saved "${name}".` })
    } catch (error) {
      setStatus({ kind: 'err', text: (error as Error).message })
    } finally {
      setBusy(false)
    }
  }, [material, model, getSessionHeaders, refresh])

  const onLoad = useCallback(async (id: string): Promise<void> => {
    if (!id) {
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const detail = await loadDesign(id, getSessionHeaders())
      if (!isValidBeamModel(detail.model)) {
        setStatus({ kind: 'err', text: `Design "${detail.name}" could not be loaded: stored model is invalid or incompatible.` })
        return
      }
      setModel(detail.model)
      persistModel(material, detail.model)
      setSelectedId(id)
      setStatus({ kind: 'ok', text: `Loaded "${detail.name}".` })
    } catch (error) {
      setStatus({ kind: 'err', text: (error as Error).message })
    } finally {
      setBusy(false)
    }
  }, [material, getSessionHeaders, setModel])

  const onDelete = useCallback(async (): Promise<void> => {
    if (!selectedId) {
      return
    }
    const target = designs.find(design => design.id === selectedId)
    if (!window.confirm(`Delete design "${target ? target.name : selectedId}"?`)) {
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      await deleteDesign(selectedId, getSessionHeaders())
      setSelectedId('')
      await refresh()
      setStatus({ kind: 'ok', text: 'Design deleted.' })
    } catch (error) {
      setStatus({ kind: 'err', text: (error as Error).message })
    } finally {
      setBusy(false)
    }
  }, [selectedId, designs, getSessionHeaders, refresh])

  return (
    <div className="design-manager-bar d-flex align-items-center gap-2 flex-wrap py-2">
      <input
        type="text"
        className="form-control form-control-sm"
        style={{ maxWidth: 220 }}
        placeholder="Design name"
        value={model.designName || ''}
        onChange={event => setModel(prev => ({ ...prev, designName: event.target.value }))}
      />
      <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={onSave}>Save design</button>
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 240 }}
        value={selectedId}
        disabled={busy}
        onChange={event => onLoad(event.target.value)}
      >
        <option value="">My designs…</option>
        {designs.map(design => (
          <option key={design.id} value={design.id}>{design.name}</option>
        ))}
      </select>
      {onShare && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={busy || !selectedId}
          onClick={() => {
            const target = designs.find(design => design.id === selectedId)
            if (target) onShare({ id: target.id, name: target.name })
          }}
        >
          Share
        </button>
      )}
      <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy || !selectedId} onClick={onDelete}>Delete</button>
      {status && (
        <span className={status.kind === 'ok' ? 'text-success small' : 'text-danger small'}>{status.text}</span>
      )}
    </div>
  )
}

export default DesignManagerBar
